import React, { useMemo } from "react"

import { PrimaryText } from "@front-shared/elements/primaryText.element.tsx"

import { NETWORK_STATUS_CONFIG } from "../../../config/networkStatus.config"
import { NetworkStatusHelper } from "../../../helpers/networkStatus.helper.ts"
import {
    NetworkStatusRuleDisplayFieldKey,
    NetworkStatusRuleRow,
} from "../../../types/networkStatus.types.ts"

export const RulesTableRowComponent: React.FunctionComponent<
    IRulesTableRowComponentProps
> = props => {
    const ruleDisplayData = useMemo(() => {
        return NetworkStatusHelper.getRuleDisplayData(props.rule)
    }, [props.rule])

    const resolvedErrorColumn =
        useMemo<NetworkStatusRuleDisplayFieldKey>(() => {
            if (!props.errorField) {
                return NETWORK_STATUS_CONFIG.defaultErrorFieldColumn
            }

            return (
                NETWORK_STATUS_CONFIG.errorFieldToColumn[props.errorField] ??
                NETWORK_STATUS_CONFIG.defaultErrorFieldColumn
            )
        }, [props.errorField])

    const showErrorInColumn = (columnKey: string): boolean => {
        return !!props.rowError && resolvedErrorColumn === columnKey
    }

    const cellContent = (
        columnKey: NetworkStatusRuleDisplayFieldKey,
        value: React.ReactNode,
        truncate?: boolean,
    ) => {
        if (!showErrorInColumn(columnKey) || !props.rowError) {
            return value
        }

        return (
            <div className="flex items-start min-w-0 flex-col">
                <div
                    className={truncate ? "min-w-0 truncate block" : "min-w-0"}
                >
                    {value}
                </div>
                <div className="text-[13px] text-brand-error2 font-medium mt-[2px]">
                    {props.rowError}
                </div>
            </div>
        )
    }

    return (
        <div className={`flex group relative ${props.className ?? ""}`}>
            <div className="table-row w-full">
                <div
                    className={`
                        min-h-[55px] px-[16px] flex items-center gap-[16px]
                        hover:bg-brand-whitePurple
                        ${props.rowError ? "bg-brand-paleRed" : "bg-transparent"}
                        ${!props.isLast ? "border-b border-solid border-brand-warmLight" : ""}
                    `}
                >
                    {props.showIdColumn && (
                        <div className="w-[80px] flex-shrink-0">
                            <PrimaryText>
                                {ruleDisplayData.id.value}
                            </PrimaryText>
                        </div>
                    )}

                    {NETWORK_STATUS_CONFIG.ruleDisplayTableRowKeys.map(
                        (dataKey, index) => {
                            const config = ruleDisplayData[dataKey]

                            return (
                                <div
                                    key={index}
                                    className={`
                                        flex-shrink-0
                                        ${config.tableWidthClassName || "w-[150px]"}
                                    `}
                                >
                                    {cellContent(
                                        dataKey,
                                        <PrimaryText>
                                            {config.value}
                                        </PrimaryText>,
                                        config.tableCellTruncate,
                                    )}
                                </div>
                            )
                        },
                    )}
                </div>
            </div>
        </div>
    )
}

interface IRulesTableRowComponentProps {
    className?: string
    rule: NetworkStatusRuleRow
    isLast: boolean
    showIdColumn: boolean
    // When set, row shows error state and error under the cell for errorField (review flow)
    rowError?: string
    // Backend field key; error is shown in the matching column
    errorField?: string
}
